// Web Audio synthesis sound manager for Palm Island
// Lazy-init AudioContext on first user gesture, voice pool, 14 synthesized sounds + spatial audio
import { eventBus, GameEvents } from './systems/events.js';

let audioCtx = null;
let isInitialized = false;

// Cached listener (camera) position for spatial audio (pi-3pk.3)
let currentListenerPosition = null;

// Voice pool — max 8 concurrent sounds, kill oldest when full
const MAX_VOICES = 8;
const activeVoices = [];

// ---------------------------------------------------------------------------
// Ambient soundscape state (pi-t0j.7)
// ---------------------------------------------------------------------------
const ambientNodes = {
  waves: null,
  wind: null,
  birdTimer: null,
  masterGain: null,
};

// ---------------------------------------------------------------------------
// AudioContext lazy-init + iOS silent-buffer prime
// ---------------------------------------------------------------------------
function ensureAudioContext() {
  if (audioCtx) return audioCtx;
  audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  // Silent buffer prime for iOS Safari latency fix
  const silentBuf = audioCtx.createBuffer(1, 1, audioCtx.sampleRate);
  const src = audioCtx.createBufferSource();
  src.buffer = silentBuf;
  src.connect(audioCtx.destination);
  src.start();
  return audioCtx;
}

function initAudioOnGesture() {
  if (isInitialized) return;
  const handler = () => {
    ensureAudioContext();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    isInitialized = true;
    document.removeEventListener('click', handler, true);
    document.removeEventListener('touchstart', handler, true);
  };
  document.addEventListener('click', handler, true);
  document.addEventListener('touchstart', handler, true);
}

// ---------------------------------------------------------------------------
// Voice pool management
// ---------------------------------------------------------------------------
function trackVoice(gainNode) {
  activeVoices.push({ gain: gainNode, startTime: audioCtx.currentTime });
  if (activeVoices.length > MAX_VOICES) {
    const oldest = activeVoices.shift();
    try {
      oldest.gain.gain.cancelScheduledValues(audioCtx.currentTime);
      oldest.gain.gain.setValueAtTime(0, audioCtx.currentTime);
      oldest.gain.disconnect();
    } catch (_) { /* already disconnected */ }
  }
}

function cleanupNodes(nodes, duration) {
  const cleanupTime = (duration + 0.1) * 1000;
  setTimeout(() => {
    nodes.forEach(node => {
      try { node.disconnect(); } catch (_) { /* already disconnected */ }
    });
    const idx = activeVoices.findIndex(v => v.gain === nodes[0]);
    if (idx !== -1) activeVoices.splice(idx, 1);
  }, cleanupTime);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function createNoiseBuffer(duration) {
  const length = Math.floor(audioCtx.sampleRate * duration);
  const buffer = audioCtx.createBuffer(1, length, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

function randomPitch(base, variance) {
  return base * (1 + (Math.random() * 2 - 1) * variance);
}

// ---------------------------------------------------------------------------
// Sound synthesis functions — each returns nothing, plays immediately
// ---------------------------------------------------------------------------

function playSkitter() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.08;

  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = createNoiseBuffer(duration);

  const bandpass = ctx.createBiquadFilter();
  bandpass.type = 'bandpass';
  bandpass.frequency.value = randomPitch(4000, 0.1);
  bandpass.Q.value = 2;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.3, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  noiseSource.connect(bandpass);
  bandpass.connect(gain);
  gain.connect(ctx.destination);
  noiseSource.start(t);
  noiseSource.stop(t + duration);

  trackVoice(gain);
  cleanupNodes([gain, bandpass, noiseSource], duration);
}

function playSplash() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.15;

  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = createNoiseBuffer(duration);

  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = randomPitch(2000, 0.1);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.4, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  noiseSource.connect(lowpass);
  lowpass.connect(gain);
  gain.connect(ctx.destination);
  noiseSource.start(t);
  noiseSource.stop(t + duration);

  trackVoice(gain);
  cleanupNodes([gain, lowpass, noiseSource], duration);
}

function playCaw() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.2;
  const baseFreq = randomPitch(800, 0.1);

  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(baseFreq * 1.5, t);
  osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.6, t + duration);

  const bandpass = ctx.createBiquadFilter();
  bandpass.type = 'bandpass';
  bandpass.frequency.value = 1200;
  bandpass.Q.value = 3;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.25, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  osc.connect(bandpass);
  bandpass.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t);
  osc.stop(t + duration);

  trackVoice(gain);
  cleanupNodes([gain, bandpass, osc], duration);
}

function playClink() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.3;
  const freq = 2000 + Math.random() * 2000; // 2-4 kHz

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = freq;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.3, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t);
  osc.stop(t + duration);

  trackVoice(gain);
  cleanupNodes([gain, osc], duration);
}

function playChime() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 1.2;
  const baseFreq = 523.25; // C5
  const harmonics = [1, 1.5, 2, 2.5, 3]; // fundamental + overtones
  const nodes = [];

  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.2, t);
  masterGain.gain.exponentialRampToValueAtTime(0.001, t + duration);
  masterGain.connect(ctx.destination);
  nodes.push(masterGain);

  harmonics.forEach((h, i) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = baseFreq * h;

    const layerGain = ctx.createGain();
    layerGain.gain.setValueAtTime(0.15 / (i + 1), t);
    layerGain.gain.exponentialRampToValueAtTime(0.001, t + duration - i * 0.1);

    osc.connect(layerGain);
    layerGain.connect(masterGain);
    osc.start(t);
    osc.stop(t + duration);

    nodes.push(osc, layerGain);
  });

  trackVoice(masterGain);
  cleanupNodes(nodes, duration);
}

function playCreak() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.5;

  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = createNoiseBuffer(duration);

  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = 300;
  lowpass.Q.value = 5;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.25, t + 0.15);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  noiseSource.connect(lowpass);
  lowpass.connect(gain);
  gain.connect(ctx.destination);
  noiseSource.start(t);
  noiseSource.stop(t + duration);

  trackVoice(gain);
  cleanupNodes([gain, lowpass, noiseSource], duration);
}

function playThunk() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.2;

  // Low sine thud
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(150, t);
  osc.frequency.exponentialRampToValueAtTime(50, t + duration);

  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(0.4, t);
  oscGain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  osc.connect(oscGain);
  oscGain.connect(ctx.destination);
  osc.start(t);
  osc.stop(t + duration);

  // Noise burst
  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = createNoiseBuffer(0.05);

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.2, t);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);

  noiseSource.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noiseSource.start(t);
  noiseSource.stop(t + 0.05);

  trackVoice(oscGain);
  cleanupNodes([oscGain, osc, noiseGain, noiseSource], duration);
}

// ---------------------------------------------------------------------------
// Beach bum hum — soft sine wave with vibrato for spatial audio hints
// Volume is dynamic (proximity-based), passed as parameter
// ---------------------------------------------------------------------------
function playHum(volume = 0.1) {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 2.0;
  const baseFreq = randomPitch(180, 0.08); // Low hum ~180Hz with slight variation

  // Main carrier oscillator — soft sine wave
  const carrier = ctx.createOscillator();
  carrier.type = 'sine';
  carrier.frequency.setValueAtTime(baseFreq, t);

  // Vibrato LFO: ±5Hz at 4Hz rate (frequency modulation)
  const vibrato = ctx.createOscillator();
  vibrato.type = 'sine';
  vibrato.frequency.value = 4; // 4Hz modulation rate
  const vibratoGain = ctx.createGain();
  vibratoGain.gain.value = 5; // ±5Hz depth
  vibrato.connect(vibratoGain);
  vibratoGain.connect(carrier.frequency);

  // Master gain — fade in, sustain, fade out
  const masterGain = ctx.createGain();
  const clampedVol = Math.min(volume, 0.15);
  masterGain.gain.setValueAtTime(0.001, t);
  masterGain.gain.linearRampToValueAtTime(clampedVol, t + 0.3);       // fade in
  masterGain.gain.setValueAtTime(clampedVol, t + duration - 0.5);     // sustain
  masterGain.gain.exponentialRampToValueAtTime(0.001, t + duration);   // fade out

  carrier.connect(masterGain);
  masterGain.connect(ctx.destination);

  carrier.start(t);
  carrier.stop(t + duration);
  vibrato.start(t);
  vibrato.stop(t + duration);

  trackVoice(masterGain);
  cleanupNodes([masterGain, carrier, vibratoGain, vibrato], duration);
}

// ---------------------------------------------------------------------------
// Guitar strum flourish — rapid 6-string arpeggio with Karplus-Strong-like
// bandpass-filtered noise bursts (pi-3pk.1)
// ---------------------------------------------------------------------------
function playGuitarFlourish() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const stringFreqs = [82.4, 110, 146.8, 196, 246.9, 329.6]; // E2 A2 D3 G3 B3 E4
  const stagger = 0.02; // 20ms between each string
  const duration = 1.2; // longest note decay
  const nodes = [];

  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.25, t);
  masterGain.connect(ctx.destination);
  nodes.push(masterGain);

  stringFreqs.forEach((freq, i) => {
    const noteStart = t + i * stagger;
    const noteDuration = 0.8 + Math.random() * 0.4; // 0.8-1.2s per string

    // Short noise burst as excitation signal
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = createNoiseBuffer(0.04);

    // Bandpass filter tuned to the string frequency (high Q for resonance)
    const bandpass = ctx.createBiquadFilter();
    bandpass.type = 'bandpass';
    bandpass.frequency.value = freq;
    bandpass.Q.value = 30;

    // Per-string gain envelope
    const stringGain = ctx.createGain();
    stringGain.gain.setValueAtTime(0.001, noteStart);
    stringGain.gain.linearRampToValueAtTime(0.5, noteStart + 0.005);
    stringGain.gain.exponentialRampToValueAtTime(0.001, noteStart + noteDuration);

    noiseSource.connect(bandpass);
    bandpass.connect(stringGain);
    stringGain.connect(masterGain);
    noiseSource.start(noteStart);
    noiseSource.stop(noteStart + noteDuration);

    nodes.push(noiseSource, bandpass, stringGain);
  });

  trackVoice(masterGain);
  cleanupNodes(nodes, duration + stringFreqs.length * stagger);
}

// ---------------------------------------------------------------------------
// Dolphin chirp/whistle — sine sweep with harmonic overlay and AM warble
// (pi-3pk.2)
// ---------------------------------------------------------------------------
function playDolphinChirp() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.6;
  const nodes = [];

  // --- Primary oscillator: 800 -> 2000 -> 1200 Hz sine sweep ---
  const primary = ctx.createOscillator();
  primary.type = 'sine';
  primary.frequency.setValueAtTime(800, t);
  primary.frequency.exponentialRampToValueAtTime(2000, t + 0.3);
  primary.frequency.exponentialRampToValueAtTime(1200, t + 0.5);

  // --- Harmonic oscillator at 1.5x frequency for richness ---
  const harmonic = ctx.createOscillator();
  harmonic.type = 'sine';
  harmonic.frequency.setValueAtTime(800 * 1.5, t);
  harmonic.frequency.exponentialRampToValueAtTime(2000 * 1.5, t + 0.3);
  harmonic.frequency.exponentialRampToValueAtTime(1200 * 1.5, t + 0.5);

  const harmonicGain = ctx.createGain();
  harmonicGain.gain.setValueAtTime(0.3, t); // harmonic is quieter
  harmonic.connect(harmonicGain);

  // --- Amplitude modulation LFO at ~8Hz for warble ---
  const lfo = ctx.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = 8;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 0.3; // modulation depth

  // AM is applied by modulating the master gain
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.2, t);
  masterGain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  lfo.connect(lfoGain);
  lfoGain.connect(masterGain.gain);

  primary.connect(masterGain);
  harmonicGain.connect(masterGain);
  masterGain.connect(ctx.destination);

  primary.start(t);
  primary.stop(t + duration);
  harmonic.start(t);
  harmonic.stop(t + duration);
  lfo.start(t);
  lfo.stop(t + duration);

  nodes.push(masterGain, primary, harmonic, harmonicGain, lfo, lfoGain);
  trackVoice(masterGain);
  cleanupNodes(nodes, duration);
}

// ---------------------------------------------------------------------------
// pi-3pk.3: Spatial audio positioning via PannerNode
// ---------------------------------------------------------------------------
export function playSoundAt(soundName, worldPosition, listenerPosition) {
  if (!worldPosition || !listenerPosition) {
    // Fall back to regular playSound if positions aren't available
    playSound(soundName);
    return;
  }
  const ctx = ensureAudioContext();
  if (!ctx) return;

  const fn = SOUNDS[soundName];
  if (!fn) return;

  // Set up AudioContext listener position (camera position)
  const listener = ctx.listener;
  if (listener.positionX) {
    // Modern API
    listener.positionX.value = listenerPosition.x;
    listener.positionY.value = listenerPosition.y;
    listener.positionZ.value = listenerPosition.z;
  } else {
    // Legacy API
    listener.setPosition(listenerPosition.x, listenerPosition.y, listenerPosition.z);
  }

  // Create PannerNode for 3D positioning
  const panner = ctx.createPanner();
  panner.panningModel = 'HRTF';
  panner.distanceModel = 'inverse';
  panner.refDistance = 5;
  panner.maxDistance = 50;
  panner.rolloffFactor = 1;

  // Set sound source position (Three.js and Web Audio use the same axes)
  if (panner.positionX) {
    panner.positionX.value = worldPosition.x;
    panner.positionY.value = worldPosition.y;
    panner.positionZ.value = worldPosition.z;
  } else {
    panner.setPosition(worldPosition.x, worldPosition.y, worldPosition.z);
  }

  // Temporarily redirect ctx.destination through the panner
  // We do this by saving the real destination, creating a temporary gain,
  // routing through panner, and calling the sound function
  const tempDest = ctx.destination;
  const routingGain = ctx.createGain();
  routingGain.gain.value = 1.0;
  routingGain.connect(panner);
  panner.connect(tempDest);

  // Override destination temporarily using a proxy approach:
  // Call the sound function, then reroute the last tracked voice through panner
  const voiceCountBefore = activeVoices.length;
  fn();
  const voiceCountAfter = activeVoices.length;

  // If a new voice was added, reroute it through the panner
  if (voiceCountAfter > voiceCountBefore) {
    const newVoice = activeVoices[activeVoices.length - 1];
    try {
      // Disconnect the gain node from destination and reconnect through panner
      newVoice.gain.disconnect(tempDest);
      newVoice.gain.connect(panner);
      panner.connect(tempDest);
    } catch (_) {
      // If disconnect fails, panner just won't apply — sound still plays normally
    }
  }
}

// ---------------------------------------------------------------------------
// pi-3pk.4: New synthesized sounds
// ---------------------------------------------------------------------------

function playBottleClink() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.15;

  // Primary sine oscillator at ~3000Hz
  const osc1 = ctx.createOscillator();
  osc1.type = 'sine';
  osc1.frequency.value = 3000;

  // Second oscillator with slight detuning for glass character
  const osc2 = ctx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.value = 3000;
  osc2.detune.value = 20; // +20 cents

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.2, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  osc1.connect(gain);
  osc2.connect(gain);
  gain.connect(ctx.destination);
  osc1.start(t);
  osc1.stop(t + duration);
  osc2.start(t);
  osc2.stop(t + duration);

  trackVoice(gain);
  cleanupNodes([gain, osc1, osc2], duration);
}

function playSecretChime() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const notes = [523, 659, 784]; // C5, E5, G5
  const stagger = 0.15;
  const noteDuration = 0.4;
  const totalDuration = stagger * (notes.length - 1) + noteDuration;
  const nodes = [];

  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.2, t);
  masterGain.connect(ctx.destination);
  nodes.push(masterGain);

  notes.forEach((freq, i) => {
    const noteStart = t + i * stagger;

    // Sine oscillator
    const oscSine = ctx.createOscillator();
    oscSine.type = 'sine';
    oscSine.frequency.value = freq;

    // Triangle oscillator for richer tone
    const oscTri = ctx.createOscillator();
    oscTri.type = 'triangle';
    oscTri.frequency.value = freq;

    const noteGain = ctx.createGain();
    noteGain.gain.setValueAtTime(0.001, noteStart);
    noteGain.gain.linearRampToValueAtTime(0.15, noteStart + 0.02);
    noteGain.gain.exponentialRampToValueAtTime(0.001, noteStart + noteDuration);

    oscSine.connect(noteGain);
    oscTri.connect(noteGain);
    noteGain.connect(masterGain);

    oscSine.start(noteStart);
    oscSine.stop(noteStart + noteDuration);
    oscTri.start(noteStart);
    oscTri.stop(noteStart + noteDuration);

    nodes.push(oscSine, oscTri, noteGain);
  });

  trackVoice(masterGain);
  cleanupNodes(nodes, totalDuration);
}

function playComboFanfare() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const notes = [261, 293, 329, 392, 523]; // C4, D4, E4, G4, C5
  const stagger = 0.12;
  const noteDuration = 0.3;
  const totalDuration = stagger * (notes.length - 1) + noteDuration + 0.5; // extra for reverb tail
  const nodes = [];

  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.25, t);
  masterGain.connect(ctx.destination);
  nodes.push(masterGain);

  // Delay-based reverb: delayed copy at 200ms with 0.3 feedback
  const delay = ctx.createDelay(1.0);
  delay.delayTime.value = 0.2;
  const feedback = ctx.createGain();
  feedback.gain.value = 0.3;
  const delayGain = ctx.createGain();
  delayGain.gain.value = 0.4; // mix level for delayed signal

  // Routing: masterGain -> delay -> feedback -> delay (loop), delay -> delayGain -> destination
  masterGain.connect(delay);
  delay.connect(feedback);
  feedback.connect(delay);
  delay.connect(delayGain);
  delayGain.connect(ctx.destination);
  nodes.push(delay, feedback, delayGain);

  notes.forEach((freq, i) => {
    const noteStart = t + i * stagger;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;

    const noteGain = ctx.createGain();
    noteGain.gain.setValueAtTime(0.001, noteStart);
    noteGain.gain.linearRampToValueAtTime(0.18, noteStart + 0.02);
    noteGain.gain.exponentialRampToValueAtTime(0.001, noteStart + noteDuration);

    osc.connect(noteGain);
    noteGain.connect(masterGain);

    osc.start(noteStart);
    osc.stop(noteStart + noteDuration);

    nodes.push(osc, noteGain);
  });

  trackVoice(masterGain);
  cleanupNodes(nodes, totalDuration);
}

function playNoteCatch() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.1;

  // Random pentatonic pitch in C5-C6 range: C5, D5, E5, G5, A5, C6
  const pentatonic = [523, 587, 659, 784, 880, 1047];
  const freq = pentatonic[Math.floor(Math.random() * pentatonic.length)];

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = freq;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.15, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t);
  osc.stop(t + duration);

  trackVoice(gain);
  cleanupNodes([gain, osc], duration);
}

// ---------------------------------------------------------------------------
// pi-3ao.10: Resource collection sounds — distinct synth per resource type
// ---------------------------------------------------------------------------

function playCollectClink() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.1;
  const pitchVariance = (Math.random() * 2 - 1) * 100; // ±100Hz

  // Primary sine at 2000Hz
  const osc1 = ctx.createOscillator();
  osc1.type = 'sine';
  osc1.frequency.value = 2000 + pitchVariance;

  // Overlay sine at 3000Hz
  const osc2 = ctx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.value = 3000 + pitchVariance;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.25, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  osc1.connect(gain);
  osc2.connect(gain);
  gain.connect(ctx.destination);
  osc1.start(t);
  osc1.stop(t + duration);
  osc2.start(t);
  osc2.stop(t + duration);

  trackVoice(gain);
  cleanupNodes([gain, osc1, osc2], duration);
}

function playCollectThunk() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.15;

  // Low woody sine at 200Hz
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(200, t);
  osc.frequency.exponentialRampToValueAtTime(80, t + duration);

  // Noise burst for attack transient
  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = createNoiseBuffer(0.04);

  // Bandpass filter at 300Hz, Q=5 for woody character
  const bandpass = ctx.createBiquadFilter();
  bandpass.type = 'bandpass';
  bandpass.frequency.value = 300;
  bandpass.Q.value = 5;

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.25, t);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.04);

  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(0.3, t);
  oscGain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  noiseSource.connect(bandpass);
  bandpass.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  osc.connect(oscGain);
  oscGain.connect(ctx.destination);

  noiseSource.start(t);
  noiseSource.stop(t + 0.04);
  osc.start(t);
  osc.stop(t + duration);

  trackVoice(oscGain);
  cleanupNodes([oscGain, osc, noiseGain, bandpass, noiseSource], duration);
}

function playCollectTinkle() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.2;
  const baseFreq = 1500;
  const nodes = [];

  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.18, t);
  masterGain.gain.exponentialRampToValueAtTime(0.001, t + duration);
  masterGain.connect(ctx.destination);
  nodes.push(masterGain);

  // Fundamental + 2x and 3x harmonics with triangle waves
  [1, 2, 3].forEach((mult, i) => {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = baseFreq * mult;

    const layerGain = ctx.createGain();
    layerGain.gain.setValueAtTime(0.12 / (i + 1), t);
    layerGain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    osc.connect(layerGain);
    layerGain.connect(masterGain);
    osc.start(t);
    osc.stop(t + duration);

    nodes.push(osc, layerGain);
  });

  trackVoice(masterGain);
  cleanupNodes(nodes, duration);
}

function playCollectShatter() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.08;

  // White noise burst
  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = createNoiseBuffer(duration);

  // Highpass at 3000Hz for crisp glass character
  const highpass = ctx.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 3000;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.3, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  noiseSource.connect(highpass);
  highpass.connect(gain);
  gain.connect(ctx.destination);
  noiseSource.start(t);
  noiseSource.stop(t + duration);

  trackVoice(gain);
  cleanupNodes([gain, highpass, noiseSource], duration);
}

function playCollectSplash() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.12;

  // Noise burst through bandpass at 800Hz for wet character
  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = createNoiseBuffer(duration);

  const bandpass = ctx.createBiquadFilter();
  bandpass.type = 'bandpass';
  bandpass.frequency.value = 800;
  bandpass.Q.value = 2;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.3, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  noiseSource.connect(bandpass);
  bandpass.connect(gain);
  gain.connect(ctx.destination);
  noiseSource.start(t);
  noiseSource.stop(t + duration);

  trackVoice(gain);
  cleanupNodes([gain, bandpass, noiseSource], duration);
}

function playCollectChime() {
  const ctx = ensureAudioContext();
  const t = ctx.currentTime;
  const duration = 0.3;
  const nodes = [];

  // Random pentatonic note in C5-C6 range
  const pentatonic = [523, 587, 659, 784, 880, 1047];
  const freq = pentatonic[Math.floor(Math.random() * pentatonic.length)];

  // Sine oscillator
  const oscSine = ctx.createOscillator();
  oscSine.type = 'sine';
  oscSine.frequency.value = freq;

  // Triangle oscillator for richer tone
  const oscTri = ctx.createOscillator();
  oscTri.type = 'triangle';
  oscTri.frequency.value = freq;

  const triGain = ctx.createGain();
  triGain.gain.value = 0.5; // triangle is quieter
  oscTri.connect(triGain);

  // Master gain with gentle decay
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.18, t);
  masterGain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  // Simple delay-based reverb for gentle shimmer
  const delay = ctx.createDelay(0.5);
  delay.delayTime.value = 0.08;
  const feedback = ctx.createGain();
  feedback.gain.value = 0.2;
  const delayGain = ctx.createGain();
  delayGain.gain.value = 0.3;

  masterGain.connect(delay);
  delay.connect(feedback);
  feedback.connect(delay);
  delay.connect(delayGain);
  delayGain.connect(ctx.destination);

  oscSine.connect(masterGain);
  triGain.connect(masterGain);
  masterGain.connect(ctx.destination);

  oscSine.start(t);
  oscSine.stop(t + duration);
  oscTri.start(t);
  oscTri.stop(t + duration);

  nodes.push(masterGain, oscSine, oscTri, triGain, delay, feedback, delayGain);
  trackVoice(masterGain);
  cleanupNodes(nodes, duration + 0.2); // extra time for reverb tail
}

// ---------------------------------------------------------------------------
// pi-t0j.7: Ambient soundscape — ocean waves, wind, intermittent birdsong
// ---------------------------------------------------------------------------

function createBrownNoiseBuffer(durationSec) {
  const length = Math.floor(audioCtx.sampleRate * durationSec);
  const buffer = audioCtx.createBuffer(1, length, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);
  let last = 0;
  for (let i = 0; i < length; i++) {
    last += (Math.random() * 2 - 1) * 0.02;
    last = Math.max(-1, Math.min(1, last));
    data[i] = last;
  }
  return buffer;
}

function startWavesLoop() {
  const ctx = audioCtx;

  // Brown noise source — 2-second looping buffer
  const buffer = createBrownNoiseBuffer(2);
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;

  // Lowpass at 600Hz — keep only low-frequency rumble
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 600;

  // Gain for wave volume
  const gain = ctx.createGain();
  gain.gain.value = 0.15;

  // LFO for slow amplitude modulation (0.08Hz — ~12.5s cycle)
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.08;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 0.05; // modulation depth
  lfo.connect(lfoGain);
  lfoGain.connect(gain.gain);

  // Panner: centered, slightly behind listener
  const panner = ctx.createStereoPanner();
  panner.pan.value = 0;

  source.connect(filter);
  filter.connect(gain);
  gain.connect(panner);
  panner.connect(ambientNodes.masterGain);

  source.start();
  lfo.start();
  ambientNodes.waves = { source, filter, gain, lfo, lfoGain, panner };
}

function startWindLoop() {
  const ctx = audioCtx;

  // White noise through lowpass for wind character
  const bufferSize = Math.floor(ctx.sampleRate * 2);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;

  // Lowpass at 800Hz with slight resonance for breathy quality
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 800;
  filter.Q.value = 2;

  // Gain for wind volume
  const gain = ctx.createGain();
  gain.gain.value = 0.08;

  // Slower LFO (0.05Hz — ~20s cycle) for gradual swell
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.05;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 0.03;
  lfo.connect(lfoGain);
  lfoGain.connect(gain.gain);

  // Stereo panner — slow random drift via second LFO
  const panner = ctx.createStereoPanner();
  panner.pan.value = 0;
  const panLfo = ctx.createOscillator();
  panLfo.frequency.value = 0.03; // very slow drift
  const panLfoGain = ctx.createGain();
  panLfoGain.gain.value = 0.7; // drift range ±0.7
  panLfo.connect(panLfoGain);
  panLfoGain.connect(panner.pan);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(panner);
  panner.connect(ambientNodes.masterGain);

  source.start();
  lfo.start();
  panLfo.start();
  ambientNodes.wind = { source, filter, gain, lfo, lfoGain, panner, panLfo, panLfoGain };
}

// Birdsong chirp patterns — 3 variations
const CHIRP_PATTERNS = [
  // Pattern 0: quick ascending chirp
  function (ctx, t, gain) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(900, t);
    osc.frequency.exponentialRampToValueAtTime(1800, t + 0.12);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.12, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.connect(g);
    g.connect(gain);
    osc.start(t);
    osc.stop(t + 0.15);
    return [osc, g];
  },
  // Pattern 1: double trill (two quick notes)
  function (ctx, t, gain) {
    const nodes = [];
    for (let i = 0; i < 2; i++) {
      const noteT = t + i * 0.15;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1200 + i * 400, noteT);
      osc.frequency.exponentialRampToValueAtTime(1600 + i * 200, noteT + 0.1);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.12, noteT);
      g.gain.exponentialRampToValueAtTime(0.001, noteT + 0.12);
      osc.connect(g);
      g.connect(gain);
      osc.start(noteT);
      osc.stop(noteT + 0.12);
      nodes.push(osc, g);
    }
    return nodes;
  },
  // Pattern 2: descending warble
  function (ctx, t, gain) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(2000, t);
    osc.frequency.exponentialRampToValueAtTime(800, t + 0.2);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.linearRampToValueAtTime(0.12, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    osc.connect(g);
    g.connect(gain);
    osc.start(t);
    osc.stop(t + 0.2);
    return [osc, g];
  },
];

function playBirdChirp() {
  if (!audioCtx || !ambientNodes.masterGain) return;
  const ctx = audioCtx;
  const t = ctx.currentTime;

  // Random stereo position
  const panner = ctx.createStereoPanner();
  panner.pan.value = Math.random() * 2 - 1; // full L/R range

  // Choose random chirp variation
  const patternIdx = Math.floor(Math.random() * CHIRP_PATTERNS.length);
  const chirpNodes = CHIRP_PATTERNS[patternIdx](ctx, t, panner);

  panner.connect(ambientNodes.masterGain);

  // Schedule cleanup
  const maxDuration = 0.35; // longest pattern is ~0.3s
  setTimeout(() => {
    chirpNodes.forEach(n => { try { n.disconnect(); } catch (_) { /* ok */ } });
    try { panner.disconnect(); } catch (_) { /* ok */ }
  }, (maxDuration + 0.1) * 1000);
}

function startBirdSongLoop() {
  function scheduleNext() {
    // Random interval between 8–20 seconds
    const delay = 8000 + Math.random() * 12000;
    ambientNodes.birdTimer = setTimeout(() => {
      playBirdChirp();
      scheduleNext();
    }, delay);
  }
  scheduleNext();
}

export function startAmbientSoundscape() {
  if (!audioCtx) return;
  if (ambientNodes.masterGain) return; // already running

  ambientNodes.masterGain = audioCtx.createGain();
  ambientNodes.masterGain.gain.value = 1.0;
  ambientNodes.masterGain.connect(audioCtx.destination);

  startWavesLoop();
  startWindLoop();
  startBirdSongLoop();
}

export function stopAmbientSoundscape() {
  // Stop waves
  if (ambientNodes.waves) {
    const w = ambientNodes.waves;
    try { w.source.stop(); } catch (_) { /* ok */ }
    try { w.lfo.stop(); } catch (_) { /* ok */ }
    [w.source, w.filter, w.gain, w.lfo, w.lfoGain, w.panner].forEach(n => {
      try { n.disconnect(); } catch (_) { /* ok */ }
    });
    ambientNodes.waves = null;
  }

  // Stop wind
  if (ambientNodes.wind) {
    const wi = ambientNodes.wind;
    try { wi.source.stop(); } catch (_) { /* ok */ }
    try { wi.lfo.stop(); } catch (_) { /* ok */ }
    try { wi.panLfo.stop(); } catch (_) { /* ok */ }
    [wi.source, wi.filter, wi.gain, wi.lfo, wi.lfoGain, wi.panner, wi.panLfo, wi.panLfoGain].forEach(n => {
      try { n.disconnect(); } catch (_) { /* ok */ }
    });
    ambientNodes.wind = null;
  }

  // Stop birdsong timer
  if (ambientNodes.birdTimer) {
    clearTimeout(ambientNodes.birdTimer);
    ambientNodes.birdTimer = null;
  }

  // Disconnect master gain
  if (ambientNodes.masterGain) {
    try { ambientNodes.masterGain.disconnect(); } catch (_) { /* ok */ }
    ambientNodes.masterGain = null;
  }
}

function duckAmbient() {
  if (!ambientNodes.masterGain || !audioCtx) return;
  const g = ambientNodes.masterGain.gain;
  g.cancelScheduledValues(audioCtx.currentTime);
  g.setValueAtTime(g.value, audioCtx.currentTime);
  g.linearRampToValueAtTime(0.5, audioCtx.currentTime + 0.05);
  g.linearRampToValueAtTime(1.0, audioCtx.currentTime + 0.55);
}

// Resource type -> collection sound mapping
const RESOURCE_COLLECT_SOUNDS = {
  coconut: playCollectThunk,
  coconut_shell: playCollectThunk,
  gem: playCollectClink,
  gold_coin: playCollectClink,
  seashell: playCollectTinkle,
  glass_shard: playCollectShatter,
  driftwood: playCollectThunk,
  fish: playCollectSplash,
  melody_fragment: playCollectChime,
  sand_dollar: playCollectTinkle,
};

export function playCollectionSound(resourceType) {
  const fn = RESOURCE_COLLECT_SOUNDS[resourceType];
  if (fn) fn();
}

// ---------------------------------------------------------------------------
// pi-t0j.8: Placed object continuous spatial sounds (campfire, wind chime)
// ---------------------------------------------------------------------------
const placedObjectSounds = new Map(); // mesh -> { type, panner, gainNode, nodes[], timers[] }
const PLACED_SOUND_MAX_ACTIVE = 2;

/**
 * Start a continuous crackling sound at a campfire's position.
 * Synthesized: noise bursts through bandpass at 1000Hz, random pops at 200-500ms.
 */
function startCampfireCrackle(mesh, position) {
  if (!audioCtx) return;
  const ctx = audioCtx;

  // PannerNode for spatial positioning
  const panner = ctx.createPanner();
  panner.panningModel = 'HRTF';
  panner.distanceModel = 'inverse';
  panner.refDistance = 5;
  panner.maxDistance = 50;
  panner.rolloffFactor = 1;
  if (panner.positionX) {
    panner.positionX.value = position.x;
    panner.positionY.value = position.y;
    panner.positionZ.value = position.z;
  } else {
    panner.setPosition(position.x, position.y, position.z);
  }

  // Master gain for this placed object (used for nearest-2 muting)
  const gainNode = ctx.createGain();
  gainNode.gain.value = 0.1;
  gainNode.connect(panner);
  panner.connect(ctx.destination);

  const timers = [];
  const nodes = [gainNode, panner];

  // Schedule recurring noise-burst crackle pops
  function schedulePop() {
    if (!placedObjectSounds.has(mesh)) return; // stopped
    const popDuration = 0.04 + Math.random() * 0.03; // 40-70ms

    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = createNoiseBuffer(popDuration);

    const bandpass = ctx.createBiquadFilter();
    bandpass.type = 'bandpass';
    bandpass.frequency.value = 800 + Math.random() * 400; // 800-1200Hz
    bandpass.Q.value = 2 + Math.random() * 2;

    const popGain = ctx.createGain();
    const t = ctx.currentTime;
    popGain.gain.setValueAtTime(0.3 + Math.random() * 0.4, t);
    popGain.gain.exponentialRampToValueAtTime(0.001, t + popDuration);

    noiseSource.connect(bandpass);
    bandpass.connect(popGain);
    popGain.connect(gainNode);
    noiseSource.start(t);
    noiseSource.stop(t + popDuration);

    // Cleanup pop nodes after they finish
    setTimeout(() => {
      try { noiseSource.disconnect(); } catch (_) { /* ok */ }
      try { bandpass.disconnect(); } catch (_) { /* ok */ }
      try { popGain.disconnect(); } catch (_) { /* ok */ }
    }, (popDuration + 0.1) * 1000);

    // Schedule next pop at 200-500ms interval
    const nextDelay = 200 + Math.random() * 300;
    const timer = setTimeout(schedulePop, nextDelay);
    timers.push(timer);
  }

  // Start the first pop
  schedulePop();

  placedObjectSounds.set(mesh, {
    type: 'campfire',
    panner,
    gainNode,
    targetVolume: 0.1,
    nodes,
    timers,
    position: { x: position.x, y: position.y, z: position.z },
  });
}

/**
 * Start a continuous tinkling sound at a wind chime's position.
 * Synthesized: random high-pitched sine notes (1500-3000Hz), 0.05-0.1s each,
 * at random 2-5s intervals.
 */
function startWindChimeTinkle(mesh, position) {
  if (!audioCtx) return;
  const ctx = audioCtx;

  // PannerNode for spatial positioning
  const panner = ctx.createPanner();
  panner.panningModel = 'HRTF';
  panner.distanceModel = 'inverse';
  panner.refDistance = 5;
  panner.maxDistance = 50;
  panner.rolloffFactor = 1;
  if (panner.positionX) {
    panner.positionX.value = position.x;
    panner.positionY.value = position.y;
    panner.positionZ.value = position.z;
  } else {
    panner.setPosition(position.x, position.y, position.z);
  }

  // Master gain for this placed object (used for nearest-2 muting)
  const gainNode = ctx.createGain();
  gainNode.gain.value = 0.08;
  gainNode.connect(panner);
  panner.connect(ctx.destination);

  const timers = [];
  const nodes = [gainNode, panner];

  // Schedule recurring tinkle notes
  function scheduleTinkle() {
    if (!placedObjectSounds.has(mesh)) return; // stopped
    const noteDuration = 0.05 + Math.random() * 0.05; // 0.05-0.1s
    const freq = 1500 + Math.random() * 1500; // 1500-3000Hz

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;

    const noteGain = ctx.createGain();
    const t = ctx.currentTime;
    noteGain.gain.setValueAtTime(0.5, t);
    noteGain.gain.exponentialRampToValueAtTime(0.001, t + noteDuration);

    osc.connect(noteGain);
    noteGain.connect(gainNode);
    osc.start(t);
    osc.stop(t + noteDuration);

    // Cleanup note nodes after they finish
    setTimeout(() => {
      try { osc.disconnect(); } catch (_) { /* ok */ }
      try { noteGain.disconnect(); } catch (_) { /* ok */ }
    }, (noteDuration + 0.1) * 1000);

    // Schedule next tinkle at 2-5s interval
    const nextDelay = 2000 + Math.random() * 3000;
    const timer = setTimeout(scheduleTinkle, nextDelay);
    timers.push(timer);
  }

  // Start the first tinkle
  scheduleTinkle();

  placedObjectSounds.set(mesh, {
    type: 'wind_chime',
    panner,
    gainNode,
    targetVolume: 0.08,
    nodes,
    timers,
    position: { x: position.x, y: position.y, z: position.z },
  });
}

/**
 * Add a continuous spatial sound for a placed object (campfire or wind chime).
 */
export function addPlacedObjectSound(mesh, recipeId, position) {
  if (!audioCtx) return;
  if (recipeId === 'campfire') {
    startCampfireCrackle(mesh, position);
  } else if (recipeId === 'wind_chime') {
    startWindChimeTinkle(mesh, position);
  }
}

/**
 * Remove and stop the continuous sound for a placed object.
 */
export function removePlacedObjectSound(mesh) {
  const sound = placedObjectSounds.get(mesh);
  if (!sound) return;

  // Clear all scheduled timers
  sound.timers.forEach(t => clearTimeout(t));

  // Disconnect all persistent nodes
  sound.nodes.forEach(n => {
    try { n.disconnect(); } catch (_) { /* ok */ }
  });

  placedObjectSounds.delete(mesh);
}

/**
 * Remove a placed object sound by its placement id (for OBJECT_REMOVED events
 * that may not carry the mesh reference).
 */
function removePlacedObjectSoundById(id) {
  for (const [mesh, sound] of placedObjectSounds) {
    if (mesh.userData && mesh.userData.placementId === id) {
      removePlacedObjectSound(mesh);
      return;
    }
  }
  // Fallback: try matching by checking mesh name or parent
  for (const [mesh] of placedObjectSounds) {
    if (mesh.name === id) {
      removePlacedObjectSound(mesh);
      return;
    }
  }
}

/**
 * Update the nearest-2-active cap for placed object sounds.
 * Call from the render loop. Mutes all except the 2 nearest to listenerPos.
 */
export function updatePlacedObjectSounds(listenerPos) {
  if (!listenerPos || placedObjectSounds.size === 0) return;

  // Calculate distances
  const entries = [];
  for (const [mesh, sound] of placedObjectSounds) {
    const pos = sound.position;
    const dx = pos.x - listenerPos.x;
    const dy = pos.y - listenerPos.y;
    const dz = pos.z - listenerPos.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    entries.push({ mesh, sound, distSq });
  }

  // Sort by distance (nearest first)
  entries.sort((a, b) => a.distSq - b.distSq);

  const now = audioCtx ? audioCtx.currentTime : 0;
  entries.forEach((entry, i) => {
    const { sound } = entry;
    const g = sound.gainNode.gain;
    if (i < PLACED_SOUND_MAX_ACTIVE) {
      // Nearest 2: restore target volume with smooth ramp
      if (g.value < sound.targetVolume * 0.5) {
        g.cancelScheduledValues(now);
        g.setValueAtTime(g.value || 0.001, now);
        g.linearRampToValueAtTime(sound.targetVolume, now + 0.3);
      }
    } else {
      // Far away: fade to near-zero
      if (g.value > 0.005) {
        g.cancelScheduledValues(now);
        g.setValueAtTime(g.value, now);
        g.linearRampToValueAtTime(0.001, now + 0.3);
      }
    }
  });
}

// ---------------------------------------------------------------------------
// Sound dispatch table
// ---------------------------------------------------------------------------
const SOUNDS = {
  skitter: playSkitter,
  splash: playSplash,
  caw: playCaw,
  clink: playClink,
  chime: playChime,
  creak: playCreak,
  thunk: playThunk,
  hum: playHum,
  guitar_flourish: playGuitarFlourish,
  dolphin_chirp: playDolphinChirp,
  bottle_clink: playBottleClink,
  secret_chime: playSecretChime,
  combo_fanfare: playComboFanfare,
  note_catch: playNoteCatch,
  collect_clink: playCollectClink,
  collect_thunk: playCollectThunk,
  collect_tinkle: playCollectTinkle,
  collect_shatter: playCollectShatter,
  collect_splash: playCollectSplash,
  collect_chime: playCollectChime,
};

// Creature species -> sound mapping
const CREATURE_SOUNDS = {
  crab: 'skitter',
  dolphin: 'splash',
  seagull: 'caw',
};

// Object id prefix -> sound mapping
const OBJECT_SOUNDS = {
  treasure_chest: 'clink',
  palmtree: 'thunk',
  welcome_sign: 'creak',
  beer_bottle: 'bottle_clink',
  beach_bum: 'chime',
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export function playSound(soundId) {
  if (!audioCtx) return;
  const fn = SOUNDS[soundId];
  if (fn) fn();
}

export function initSounds() {
  initAudioOnGesture();

  eventBus.on(GameEvents.CREATURE_CLICKED, (data) => {
    duckAmbient();
    const soundId = CREATURE_SOUNDS[data.species];
    if (!soundId) return;
    // Use spatial audio if mesh position is available
    if (data.mesh && data.mesh.position && currentListenerPosition) {
      playSoundAt(soundId, data.mesh.position, currentListenerPosition);
    } else {
      playSound(soundId);
    }
  });

  eventBus.on(GameEvents.OBJECT_CLICKED, (data) => {
    duckAmbient();
    // Match object id by prefix (e.g. "palmtree_0" -> "palmtree")
    const id = data.id || '';
    const key = Object.keys(OBJECT_SOUNDS).find(k => id === k || id.startsWith(k + '_'));
    if (key) playSound(OBJECT_SOUNDS[key]);
  });

  eventBus.on(GameEvents.RESOURCE_COLLECTED, (data) => {
    duckAmbient();
    playCollectionSound(data.resourceType);
  });

  eventBus.on(GameEvents.DISCOVERY_NEW, (data) => {
    if (data && data.category === 'secrets') {
      playSound('secret_chime');
    } else if (data && data.category === 'combos') {
      playSound('combo_fanfare');
    } else {
      playSound('chime');
    }
  });

  // pi-t0j.8: Wire placed-object spatial sounds
  eventBus.on(GameEvents.OBJECT_PLACED, (data) => {
    if (data.mesh && data.recipeId) {
      addPlacedObjectSound(data.mesh, data.recipeId, data.position);
    }
  });

  eventBus.on(GameEvents.OBJECT_REMOVED, (data) => {
    if (data.id) {
      removePlacedObjectSoundById(data.id);
    }
    // Also try by recipeId as fallback — iterate and remove matching type
    if (data.recipeId) {
      for (const [mesh, sound] of placedObjectSounds) {
        if (sound.type === data.recipeId) {
          removePlacedObjectSound(mesh);
          break;
        }
      }
    }
  });

  // Start ambient soundscape after first user gesture resumes AudioContext
  const startAmbientOnGesture = () => {
    if (audioCtx && audioCtx.state === 'running') {
      startAmbientSoundscape();
      document.removeEventListener('click', startAmbientOnGesture, true);
      document.removeEventListener('touchstart', startAmbientOnGesture, true);
    }
  };
  document.addEventListener('click', startAmbientOnGesture, true);
  document.addEventListener('touchstart', startAmbientOnGesture, true);

  console.log('[Sounds] System initialized');
}

// ---------------------------------------------------------------------------
// pi-t0j.9: Camera-distance crossfade — zoom out = more ocean, zoom in = more land
// ---------------------------------------------------------------------------
function updateAmbientCrossfade(cameraPosition) {
  if (!ambientNodes.masterGain || !audioCtx) return;

  // Calculate horizontal distance from island center (0, 0, 0)
  const dist = Math.sqrt(
    cameraPosition.x * cameraPosition.x +
    cameraPosition.z * cameraPosition.z
  );

  // Normalize: close (15) = 0, far (80) = 1
  const t = Math.max(0, Math.min(1, (dist - 15) / 65));

  // Zoom out (far): more waves, less wind/birds
  // Zoom in (close): less waves, more wind/birds
  if (ambientNodes.waves) {
    ambientNodes.waves.gain.gain.setTargetAtTime(0.08 + t * 0.12, audioCtx.currentTime, 0.3);
  }
  if (ambientNodes.wind) {
    ambientNodes.wind.gain.gain.setTargetAtTime(0.10 - t * 0.06, audioCtx.currentTime, 0.3);
  }
}

// Update the cached listener position for spatial audio (call from render loop)
export function updateListenerPosition(position) {
  if (position) {
    currentListenerPosition = { x: position.x, y: position.y, z: position.z };
    // Update nearest-2 cap for placed object sounds (pi-t0j.8)
    updatePlacedObjectSounds(currentListenerPosition);
    // Crossfade ambient layers based on camera distance (pi-t0j.9)
    updateAmbientCrossfade(currentListenerPosition);
  }
}

// Exported directly for proximity-based volume control (pi-d7w.4)
export { playHum };
// Exported for direct use in main.js (pi-3pk.1, pi-3pk.2)
export { playGuitarFlourish, playDolphinChirp };
// Exported for direct use in main.js (pi-3pk.4)
export { playNoteCatch };
